import React, { useState, useEffect} from "react";
import {
  AppBar,
  Box,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Menu,
  Container,
  Avatar,
  Button,
  Tooltip,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,

  Slide,
  useTheme,
  useMediaQuery,
  Paper,
  
  keyframes,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Login as LoginIcon,
  Person as PersonIcon,
  AccountCircle as AccountCircleIcon,
  Settings as SettingsIcon,
  SupportAgent as SupportAgentIcon,
  Logout as LogoutIcon,
  BarChart as BarChartIcon,

  Wifi as ConnectedIcon,
  WifiOff as DisconnectedIcon,
  Star as StarIcon,
  EmojiEvents as TrophyIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";
import { useAuth } from "../AuthContext";
import i18n from "../services/i18n";
import { useTranslation } from "react-i18next";
import OAuthSignInPage from "./OAuthSignInPage";
import Profile from "./Profile";
import { sendMessage, resetWebSocket } from "../services/websocketManager";
import { generalMessageTemplate } from "../entities/generalMessageTemplate";

import { addListener, removeListener } from "../services/websocketManager";
import { useUser } from "../authMethods/UserContext";
import NotEnoughCoinsDialog from "./NotEnoughCoinsDialog";
import { GENERAL } from "../general/General";


const AppVersion = "v1.3.0";

// Animation keyframes
const pulseGlow = keyframes`
  0%, 100% { 
    box-shadow: 0 0 5px rgba(255, 215, 0, 0.5);
  }
  50% { 
    box-shadow: 0 0 20px rgba(255, 215, 0, 0.8), 0 0 30px rgba(255, 215, 0, 0.6);
  }
`;

const coinSpin = keyframes`
  0% { transform: rotateY(0deg); }
  100% { transform: rotateY(360deg); }
`;

const slideDown = keyframes`
  0% { transform: translateY(-100%); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
`;

const pages = [
  {
    name: "Welcome",
    route: "/welcome",
    icon: "🏠",
    color: "#4f46e5",
    gradient: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)"
  },
  {
    name: "Play",
    route: "/main",
    icon: "⚔️",
    color: "#059669",
    gradient: "linear-gradient(135deg, #059669 0%, #047857 100%)"
  },
  {
    name: "Practice",
    route: "/learn",
    icon: "🎓",
    color: "#dc2626",
    gradient: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)"
  },
  {
    name: "My Games",
    route: "/games",
    icon: "📚",
    color: "#7c2d12",
    gradient: "linear-gradient(135deg, #7c2d12 0%, #92400e 100%)"
  },
  {
    name: "Favorites",
    route: "/favorites",
    icon: "❤️",
    color: "#be185d",
    gradient: "linear-gradient(135deg, #be185d 0%, #ec4899 100%)"
  },
];

function ResponsiveAppBar({ websocketStatus }) {
  const { userData } = useUser();
  const { currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isVerySmall = useMediaQuery(theme.breakpoints.down("sm"));
  
  // State management
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [, setIsUserLoggedIn] = useState(false);
  const [showLoginPage, setShowLoginPage] = useState(false);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState({
    code: "en",
    flag: "https://res.cloudinary.com/dnlgtek2a/image/upload/v1743405788/Pictures/ciqsflhvjmllwogcd2fg.svg",
  });
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);

  const location = useLocation();
  const auth = getAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();


  // Connection status
  const isConnected = websocketStatus === "open";
  const statusMessage = isConnected
    ? t("Connected to the server")
    : t("Disconnected from the server");

  // User stats calculations
  

  const winRate = userData?.userStatistics?.totalGames > 0 ? 
  Math.round((userData?.userStatistics?.totalWins / userData?.userStatistics?.totalGames) * 10000) : 0;

  
  const isPerfectRecord = winRate === "1.00" && userData?.userStatistics?.totalGames > 0;

  // Effects
  useEffect(() => {
    const handleWebSocketMessage = (event) => {
      if (event.type === "message" && event.message.messageType === "user_logged_out") {
        setSelectedUser(null);
        setIsProfileDialogOpen(false);
        setIsUserLoggedIn(false);
      }
    };

    addListener(handleWebSocketMessage);
    return () => removeListener(handleWebSocketMessage);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setIsUserLoggedIn(!!currentUser);
    });

    const persistedLanguage = localStorage.getItem("selectedLanguage");
    if (persistedLanguage) {
      const lang = JSON.parse(persistedLanguage);
      i18n.changeLanguage(lang.code);
      setSelectedLanguage(lang);
    }

    return unsubscribe;
  }, [auth]);

  // Handlers
  const handleOpenUserMenu = (event) => setAnchorElUser(event.currentTarget);
  const handleCloseUserMenu = () => setAnchorElUser(null);

  const handleLogin = () => navigate("/login");

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setIsUserLoggedIn(false);
      
      const generalMessage = { ...generalMessageTemplate };
      generalMessage.messageType = "user_logged_out";
      sendMessage(generalMessage);
      
      resetWebSocket();
      localStorage.removeItem("uncompleted_game");
      localStorage.removeItem("browserId");
      window.location.href = "https://landing.democraticchess.com/";
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const handleLanguageChange = (langCode, flag) => {
    const selectedLang = { code: langCode, flag };
    i18n.changeLanguage(langCode);
    setSelectedLanguage(selectedLang);
    setLanguageMenuAnchor(null);
    localStorage.setItem("selectedLanguage", JSON.stringify(selectedLang));
  };

  const handleProfile = () => {
    setSelectedUser(userData);
    setIsProfileDialogOpen(true);
  };

  const navigationItems = [
    { label: t("Profile"), icon: <PersonIcon />, action: handleProfile },
    { label: t("Account"), icon: <AccountCircleIcon />, action: () => navigate("/account") },
    { label: t("Statistics"), icon: <BarChartIcon />, action: () => navigate("/charts") },
    { label: t("Settings"), icon: <SettingsIcon />, action: () => navigate("/settings") },
    { label: t("Technical Support"), icon: <SupportAgentIcon />, action: () => navigate("/contactus") },
  ];

  // Mobile Navigation Drawer
  const MobileDrawer = () => (
    <Drawer
      anchor="left"
      open={mobileDrawerOpen}
      onClose={() => setMobileDrawerOpen(false)}
      PaperProps={{
        sx: {
          width: 280,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
        }
      }}
    >
      <Box sx={{ p: 3, textAlign: "center", borderBottom: "1px solid rgba(255,255,255,0.2)" }}>
        <Typography variant="h6" fontWeight="bold">
          {t("DEMOCRATIC CHESS")}
        </Typography>
      </Box>
      
      <List sx={{ p: 0 }}>
        {pages.map((page, index) => (
          <Slide key={page.name} direction="right" in timeout={300 + index * 100}>
            <ListItem
              button
              onClick={() => {
                navigate(page.route);
                setMobileDrawerOpen(false);
              }}
              sx={{
                py: 2,
                px: 3,
                background: location.pathname === page.route 
                  ? "rgba(255,255,255,0.2)" 
                  : "transparent",
                borderLeft: location.pathname === page.route 
                  ? "4px solid #ffd700" 
                  : "4px solid transparent",
                "&:hover": {
                  background: "rgba(255,255,255,0.1)",
                },
                transition: "all 0.3s ease",
              }}
            >
              <ListItemIcon sx={{ color: "white", minWidth: 40 }}>
                <Typography fontSize="1.5rem">{page.icon}</Typography>
              </ListItemIcon>
              <ListItemText 
                primary={t(page.name)}
                sx={{
                  "& .MuiListItemText-primary": {
                    fontWeight: location.pathname === page.route ? 600 : 400,
                  }
                }}
              />
            </ListItem>
          </Slide>
        ))}
      </List>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.2)", mx: 2 }} />
      
      <List>
        {navigationItems.map((item, index) => (
          <ListItem 
            key={item.label}
            button 
            onClick={() => {
              item.action();
              setMobileDrawerOpen(false);
              handleCloseUserMenu();
            }}
            sx={{ py: 1.5, px: 3 }}
          >
            <ListItemIcon sx={{ color: "white", minWidth: 40 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
        
        {!(window.FBInstant && window.FB_USER_DATA) && (window.Telegram && window.Telegram.WebApp) && (
          <ListItem 
            button 
            onClick={() => {
              handleLogout();
              setMobileDrawerOpen(false);
            }}
            sx={{ py: 1.5, px: 3 }}
          >
            <ListItemIcon sx={{ color: "white", minWidth: 40 }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary={t("Logout")} />
          </ListItem>
        )}
      </List>
    </Drawer>
  );

  if (showLoginPage) {
    return <OAuthSignInPage onBack={() => setShowLoginPage(false)} />;
  }

  return (
    <>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          height: { xs: "60px", md: "70px" },
          animation: `${slideDown} 0.6s ease-out`,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar 
            disableGutters
            sx={{ 
              height: "100%",
              justifyContent: "space-between",
              px: { xs: 1, md: 2 }
            }}
          >
            {/* Left Section - Logo & Mobile Menu */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {isMobile && (
                <IconButton
                  onClick={() => setMobileDrawerOpen(true)}
                  sx={{ 
                    color: "white",
                    background: "rgba(255,255,255,0.1)",
                    "&:hover": { background: "rgba(255,255,255,0.2)" }
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}
              
              {!isVerySmall && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    component="img"
                    src="https://res.cloudinary.com/dnlgtek2a/image/upload/v1743427691/Pictures/liberty_1.png"
                    alt="Logo"
                    sx={{
                      height: { xs: 32, md: 44 },
                      width: { xs: 32, md: 44 },
                      filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
                    }}
                  />
                  {!isMobile && (
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: "monospace",
                        fontWeight: 800,
                        letterSpacing: ".2rem",
                        fontSize: "0.9rem",
                        textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                      }}
                    >
                      {t("DEMOCRATIC CHESS")}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>

            {/* Center Section - Desktop Navigation */}
            {!isMobile && (
              <Box 
                sx={{ 
                  display: "flex", 
                  gap: 1,
                  background: "rgba(255,255,255,0.1)",
                  borderRadius: "25px",
                  p: 1,
                  backdropFilter: "blur(10px)",
                }}
                data-tour="navigation-menu"
              >
                {pages.map((page) => {
                  const isActive = location.pathname === page.route;
                  return (
                    <Tooltip key={page.name} title={t(page.name)}>
                      <Button
                        onClick={() => navigate(page.route)}
                        sx={{
                          minWidth: 60,
                          height: 50,
                          borderRadius: "20px",
                          background: isActive 
                            ? "rgba(255,255,255,0.2)" 
                            : "transparent",
                          border: isActive 
                            ? "2px solid rgba(255,215,0,0.8)" 
                            : "2px solid transparent",
                          transform: isActive ? "scale(1.05)" : "scale(1)",
                          animation: isActive ? `${pulseGlow} 2s infinite` : "none",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            background: "rgba(255,255,255,0.15)",
                            transform: "scale(1.05)",
                          },
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        <Typography fontSize="1.2rem">{page.icon}</Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontSize: "0.7rem",
                            fontWeight: isActive ? 600 : 400,
                            color: "white",
                          }}
                        >
                          {t(page.name)}
                        </Typography>
                      </Button>
                    </Tooltip>
                  );
                })}
              </Box>
            )}

            {/* Right Section - Controls */}
            <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, md: 2 } }}>
              {/* Language Selector */}
              <Tooltip title={t("selectLanguage")}>
                <IconButton
                  onClick={(e) => setLanguageMenuAnchor(e.currentTarget)}
                  sx={{
                    background: "rgba(255,255,255,0.1)",
                    "&:hover": { background: "rgba(255,255,255,0.2)" },
                    p: 1,
                  }}
                  data-tour="language-selector"
                >
                  <Box
                    component="img"
                    src={selectedLanguage.flag}
                    alt={selectedLanguage.code}
                    sx={{ width: 20, height: 20, borderRadius: "4px" }}
                  />
                </IconButton>
              </Tooltip>

              {/* Connection Status */}
              <Tooltip title={statusMessage}>
                <IconButton
                  onClick={() => setStatusDialogOpen(true)}
                  sx={{
                    color: isConnected ? "#4ade80" : "#f87171",
                    background: "rgba(255,255,255,0.1)",
                    "&:hover": { background: "rgba(255,255,255,0.2)" },
                  }}
                  data-tour="connection-status"
                >
                  {isConnected ? <ConnectedIcon /> : <DisconnectedIcon />}
                </IconButton>
              </Tooltip>

              {/* User Balance */}
              <Paper
                onClick={() => setIsDialogOpen(true)}
                sx={{
                  background: "linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)",
                  color: "#1a1a1a",
                  px: { xs: 1, md: 1.5 },
                  py: 0.5,
                  borderRadius: "20px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 25px rgba(255, 215, 0, 0.4)",
                  },
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  minWidth: { xs: 80, md: 120 },
                }}
                data-tour="user-balance"
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {isPerfectRecord ? (
                    <TrophyIcon sx={{ fontSize: { xs: "1.1rem", md: "1.3rem" } }} />
                  ) : (
                    <StarIcon sx={{ fontSize: { xs: "1.1rem", md: "1.3rem" } }} />
                  )}
                  <Typography 
                    variant="body2" 
                    fontWeight="bold" 
                    fontSize={{ xs: "0.8rem", md: "0.9rem" }}
                  >
                    {winRate}
                  </Typography>
                </Box>

                <Divider orientation="vertical" flexItem />

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    component="img"
                    src="https://res.cloudinary.com/dnlgtek2a/image/upload/v1743405234/Pictures/coin.png"
                    alt="Coins"
                    sx={{ 
                      width: { xs: 16, md: 20 }, 
                      height: { xs: 16, md: 20 },
                      animation: `${coinSpin} 3s linear infinite`,
                    }}
                  />
                  <Typography 
                    variant="body2" 
                    fontWeight="bold"
                    fontSize={{ xs: "0.8rem", md: "0.9rem" }}
                  >
                    {userData?.userStatistics?.coins ?? 0}
                  </Typography>
                </Box>
              </Paper>

              {/* User Menu */}
              {userData?.socialUser || (window.FBInstant && window.FB_USER_DATA) ? (
                <Box data-tour="user-menu">
                  <Tooltip title={t("Open settings")}>
                    <IconButton
                      onClick={handleOpenUserMenu}
                      sx={{
                        p: 0.5,
                        background: "rgba(255,255,255,0.1)",
                        border: "2px solid rgba(255,255,255,0.2)",
                        borderRadius: "50%",
                        "&:hover": { 
                          background: "rgba(255,255,255,0.2)",
                          border: "2px solid rgba(255,255,255,0.4)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      <Avatar
                        alt={currentUser?.displayName || "Guest"}
                        src={
                          userData.url ||
                          currentUser?.photoURL ||
                          "https://res.cloudinary.com/dnlgtek2a/image/upload/v1743405455/Pictures/mt3ulobaoutca6qoeokv.png"
                        }
                        sx={{ 
                          width: { xs: 32, md: 40 }, 
                          height: { xs: 32, md: 40 },
                          border: "2px solid rgba(255,255,255,0.3)",
                        }}
                      />
                    </IconButton>
                  </Tooltip>

                  <Menu
                    anchorEl={anchorElUser}
                    open={Boolean(anchorElUser)}
                    onClose={handleCloseUserMenu}
                    PaperProps={{
                      sx: {
                        mt: 1,
                        borderRadius: "12px",
                        background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 100%)",
                        backdropFilter: "blur(20px)",
                        border: "1px solid rgba(255,255,255,0.2)",
                        minWidth: 200,
                      }
                    }}
                  >
                    {navigationItems.map((item) => (
                      <MenuItem
                        key={item.label}
                        onClick={() => {
                          item.action();
                          handleCloseUserMenu();
                        }}
                        sx={{
                          gap: 2,
                          py: 1.5,
                          borderRadius: "8px",
                          mx: 1,
                          my: 0.5,
                          "&:hover": {
                            background: "linear-gradient(135deg, #667eea20 0%, #764ba220 100%)",
                          },
                        }}
                      >
                        {item.icon}
                        <Typography>{item.label}</Typography>
                      </MenuItem>
                    ))}
                    
                    {!(window.FBInstant && window.FB_USER_DATA) && (window.Telegram && window.Telegram.WebApp) && (
                      <>
                        <Divider sx={{ mx: 1, my: 0.5 }} />
                        <MenuItem
                          onClick={() => {
                            handleLogout();
                            handleCloseUserMenu();
                          }}
                          sx={{
                            gap: 2,
                            py: 1.5,
                            borderRadius: "8px",
                            mx: 1,
                            my: 0.5,
                            color: "#ef4444",
                            "&:hover": {
                              background: "rgba(239, 68, 68, 0.1)",
                            },
                          }}
                        >
                          <LogoutIcon />
                          <Typography>{t("Logout")}</Typography>
                        </MenuItem>
                      </>
                    )}
                  </Menu>
                </Box>
              ) : (
                <Tooltip title={t("Login")}>
                  <Button
                    onClick={handleLogin}
                    startIcon={<LoginIcon />}
                    variant="contained"
                    sx={{
                      background: "rgba(255,255,255,0.2)",
                      color: "white",
                      borderRadius: "20px",
                      "&:hover": {
                        background: "rgba(255,255,255,0.3)",
                      },
                    }}
                  >
                    {!isVerySmall && t("Login")}
                  </Button>
                </Tooltip>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <MobileDrawer />

      {/* Language Menu */}
      <Menu
        anchorEl={languageMenuAnchor}
        open={Boolean(languageMenuAnchor)}
        onClose={() => setLanguageMenuAnchor(null)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            background: "rgba(255,255,255,0.95)",
            backdropFilter: "blur(20px)",
          }
        }}
      >
        {GENERAL.languageOptions.map((lang) => (
          <MenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code, lang.flag)}
            sx={{
              gap: 2,
              borderRadius: "8px",
              mx: 1,
              my: 0.5,
            }}
          >
            <Box
              component="img"
              src={lang.flag}
              alt={lang.name}
              sx={{ width: 20, height: 20, borderRadius: "4px" }}
            />
            {lang.name}
          </MenuItem>
        ))}
      </Menu>

      {/* Status Dialog */}
      <Dialog 
        open={statusDialogOpen} 
        onClose={() => setStatusDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 100%)",
            backdropFilter: "blur(20px)",
          }
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isConnected ? <ConnectedIcon color="success" /> : <DisconnectedIcon color="error" />}
          {t("Connection Status")}
        </DialogTitle>
        <DialogContent>
          <Typography 
            variant="body1" 
            sx={{ color: isConnected ? "#059669" : "#dc2626", mb: 2 }}
          >
            {statusMessage}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t("Version")}: {AppVersion}
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Dialogs */}
      <NotEnoughCoinsDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        buy={true}
        userData={userData}
      />

      <Profile
        open={isProfileDialogOpen}
        onClose={() => setIsProfileDialogOpen(false)}
        avatar={selectedUser}
        userData={userData}
        onAddToFavorite={() => setIsProfileDialogOpen(false)}
      />
    </>
  );
}

export default ResponsiveAppBar;